// Lightweight i18n helper — adds Hindi / Tamil / Telugu translations for the
// most frequently-seen UI strings. Components opt-in via `t(key)`. Strings not
// present in the selected dictionary fall back to English.

import { useEffect, useState } from 'react';

const DICTS = {
  en: {},
  hi: {
    Dashboard: 'डैशबोर्ड', Calendar: 'कैलेंडर', Patients: 'मरीज़', Reports: 'रिपोर्ट',
    Admin: 'व्यवस्थापक', Settings: 'सेटिंग्स', Save: 'सहेजें', Cancel: 'रद्द करें',
    Delete: 'हटाएं', Edit: 'संपादित करें', Add: 'जोड़ें', Search: 'खोजें',
    Name: 'नाम', Phone: 'फ़ोन', Age: 'उम्र', Gender: 'लिंग', Address: 'पता',
    Male: 'पुरुष', Female: 'महिला', Today: 'आज',
    'Appointments': 'अपॉइंटमेंट', 'Treatments': 'उपचार', 'Bills': 'बिल', 'Payments': 'भुगतान',
    'Total': 'कुल', 'Paid': 'भुगतान किया', 'Outstanding': 'बकाया',
    'New Patient': 'नया मरीज़', 'Select Patient': 'मरीज़ चुनें',
    'Logout': 'लॉगआउट', 'Logged in as': 'के रूप में लॉग इन',
  },
  ta: {
    Dashboard: 'டாஷ்போர்டு', Calendar: 'காலண்டர்', Patients: 'நோயாளிகள்', Reports: 'அறிக்கைகள்',
    Admin: 'நிர்வாகி', Settings: 'அமைப்புகள்', Save: 'சேமி', Cancel: 'ரத்து',
    Delete: 'நீக்கு', Edit: 'திருத்து', Add: 'சேர்', Search: 'தேடு',
    Name: 'பெயர்', Phone: 'தொலைபேசி', Age: 'வயது', Gender: 'பாலினம்', Address: 'முகவரி',
    Male: 'ஆண்', Female: 'பெண்', Today: 'இன்று',
    'Appointments': 'சந்திப்புகள்', 'Treatments': 'சிகிச்சைகள்', 'Bills': 'பில்கள்', 'Payments': 'பணம் செலுத்தல்',
    'Total': 'மொத்தம்', 'Paid': 'செலுத்தப்பட்டது', 'Outstanding': 'நிலுவை',
    'New Patient': 'புதிய நோயாளி', 'Select Patient': 'நோயாளியைத் தேர்வு செய்',
    'Logout': 'வெளியேறு', 'Logged in as': 'உள்நுழைந்தவர்',
  },
  te: {
    Dashboard: 'డాష్‌బోర్డ్', Calendar: 'క్యాలెండర్', Patients: 'రోగులు', Reports: 'నివేదికలు',
    Admin: 'నిర్వాహకుడు', Settings: 'సెట్టింగులు', Save: 'సేవ్', Cancel: 'రద్దు',
    Delete: 'తొలగించు', Edit: 'సవరించు', Add: 'జోడించు', Search: 'వెతుకు',
    Name: 'పేరు', Phone: 'ఫోన్', Age: 'వయస్సు', Gender: 'లింగం', Address: 'చిరునామా',
    Male: 'పురుషుడు', Female: 'స్త్రీ', Today: 'ఈరోజు',
    'Appointments': 'అపాయింట్‌మెంట్లు', 'Treatments': 'చికిత్సలు', 'Bills': 'బిల్లులు', 'Payments': 'చెల్లింపులు',
    'Total': 'మొత్తం', 'Paid': 'చెల్లించబడింది', 'Outstanding': 'బకాయి',
    'New Patient': 'కొత్త రోగి', 'Select Patient': 'రోగిని ఎంచుకోండి',
    'Logout': 'లాగ్అవుట్', 'Logged in as': 'లాగిన్ అయ్యారు',
  },
};

export const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
];

export function getLang() {
  return localStorage.getItem('oralzy_lang') || 'en';
}

export function setLang(code) {
  localStorage.setItem('oralzy_lang', code);
  window.dispatchEvent(new Event('oralzy_lang_changed'));
}

export function t(key) {
  const lang = getLang();
  const d = DICTS[lang] || {};
  return d[key] || key;
}

// React hook for components — re-renders when user changes language
export function useLang() {
  const [, tick] = useState(0);
  useEffect(() => {
    const h = () => tick(v => v + 1);
    window.addEventListener('oralzy_lang_changed', h);
    return () => window.removeEventListener('oralzy_lang_changed', h);
  }, []);
  return { t, lang: getLang(), setLang };
}
